import {
  ArrowClockwiseIcon,
  FileDocIcon,
  FilePdfIcon,
  FileTextIcon,
  FileVideoIcon,
  TrashSimpleIcon,
} from '@phosphor-icons/react';
import type { MouseEvent } from 'react';

import { CloseButton } from '@/components/common/close-button';

import styles from './upload.module.scss';

// Remove/retry live inside the Dragger's click surface in the collapsed layout, which
// would otherwise re-open the file picker. Same pattern as <Select>'s tag remove.
function stopBubbling(action: () => void) {
  return (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    action();
  };
}

export type UploadEntry = {
  uid: string;
  name: string;
  size: number;
  status: 'uploading' | 'success' | 'error';
  percent: number;
  error?: string;
  url?: string;
  preview?: string;
  retriable?: boolean;
};

function formatSize(bytes: number) {
  return `${(bytes / 1024 / 1024).toFixed(1)}Mb`;
}

/** Figma 12:163611 draws the file's own kind inside the tile — a PDF page, a video frame —
 *  rather than one generic document glyph. Keyed off the extension because the committed
 *  entry carries a name, not the original File's MIME type. */
function FileGlyph({ name }: { name: string }) {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  if (ext === 'pdf') return <FilePdfIcon size={20} aria-hidden />;
  if (ext === 'mp4' || ext === 'mov') return <FileVideoIcon size={20} aria-hidden />;
  if (ext === 'doc' || ext === 'docx') return <FileDocIcon size={20} aria-hidden />;
  return <FileTextIcon size={20} aria-hidden />;
}

type Props = {
  variant: 'image' | 'document';
  entry: UploadEntry;
  onRemove: (uid: string) => void;
  onRetry: (uid: string) => void;
};

export function UploadItem({ variant, entry, onRemove, onRetry }: Props) {
  if (variant === 'image') {
    // A rejection carries a sentence, not a picture. The marker lets the collapsed layout give it
    // the full grid row instead of squeezing it into a thumbnail-sized square (it is inert
    // elsewhere, so the catalog upload zones keep their square error tile).
    const isRejection = entry.status === 'error' && !entry.retriable;
    return (
      <div className={[styles.tile, isRejection ? styles.tileRejected : undefined].join(' ')}>
        {entry.status === 'uploading' ? (
          <div className={styles.tileLoading}>{entry.percent}%</div>
        ) : entry.status === 'error' && entry.retriable ? (
          <button
            type="button"
            className={styles.tileLoading}
            onClick={stopBubbling(() => onRetry(entry.uid))}
          >
            <ArrowClockwiseIcon size={16} aria-hidden /> Retry
          </button>
        ) : entry.status === 'error' ? (
          <div className={styles.tileLoading}>{entry.error ?? 'Error'}</div>
        ) : (
          <img src={entry.preview ?? entry.url} alt={entry.name} />
        )}
        <div className={styles.tileRemove}>
          <CloseButton
            aria-label={`Remove ${entry.name}`}
            onClick={stopBubbling(() => onRemove(entry.uid))}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <span className={styles.rowIcon}>
        <FileGlyph name={entry.name} />
      </span>
      <div className={styles.rowBody}>
        <div className={styles.rowName}>{entry.name}</div>
        {entry.status === 'error' ? (
          <div className={styles.rowError}>{entry.error ?? 'Upload failed'}</div>
        ) : (
          <div className={styles.rowMeta}>{formatSize(entry.size)}</div>
        )}
        {entry.status === 'uploading' ? (
          <div className={styles.bar}>
            <span style={{ width: `${entry.percent}%` }} />
          </div>
        ) : null}
      </div>
      {entry.status === 'error' && entry.retriable ? (
        <button
          type="button"
          className={styles.retry}
          onClick={stopBubbling(() => onRetry(entry.uid))}
        >
          <ArrowClockwiseIcon size={16} aria-hidden /> Retry
        </button>
      ) : null}
      <button
        type="button"
        className={styles.rowRemove}
        aria-label={`Remove ${entry.name}`}
        onClick={stopBubbling(() => onRemove(entry.uid))}
      >
        <TrashSimpleIcon size={16} aria-hidden />
      </button>
    </div>
  );
}
